/**
 * #427 W3 — Timesheet approval modal (spec §5 W3, D3).
 *
 * Walks each FLAGGED rest instance and makes the manager attest it
 * provided (✓) or missed (✗) before the timesheet can be approved. Only
 * attested-MISSED periods become owed wages in the export (D3) — a missing
 * punch alone never does. Open entries block approval with a link to the
 * fix flow (no dead ends, P2).
 *
 * Design Philosophy principles served:
 *  - P2 (next action obvious): the modal's single filled-primary is
 *    "Approve timesheet", disabled until EVERY flagged instance is attested;
 *    an open punch replaces the form with a clear route to Punch records.
 *  - P5 (never lie about data): the owed-wages preview shows an honest
 *    "can't calculate — no rate set" when there is no rate, never a fake $0.
 *  - Five states: default (attestation walk) · pending (submitting) · error
 *    (recovery copy + retry) · blocked (open entry) · trivial (no flags →
 *    straight confirm). Loading/stale N/A — the row is already in hand.
 */
import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Modal } from '@ferro/ui';
import {
  adminTimesheetsApi,
  type RestAttestationInput,
  type TimesheetRowView,
} from '../../lib/api.js';
import { atRiskCents, formatUsdCents } from '../../lib/timesheet-format.js';

export interface TimesheetApprovalModalProps {
  apiUrl: string;
  row: TimesheetRowView;
  onClose: () => void;
  onApproved: (row: TimesheetRowView) => void;
}

/** One attestable instance = a single missed-rest slot on a workday. A day
 *  with `missing: 2` yields two instances, each attested independently. */
type Instance = { key: string; date: string };

function fmtDay(dateKey: string): string {
  const d = new Date(`${dateKey}T12:00:00`);
  if (Number.isNaN(d.getTime())) return dateKey;
  return d.toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
  });
}

export function TimesheetApprovalModal({
  apiUrl,
  row,
  onClose,
  onApproved,
}: TimesheetApprovalModalProps): JSX.Element {
  const blockedByOpenEntry = row.flags.includes('open_entry');

  // Flatten each flagged workday into its individual missed-rest instances.
  const instances = useMemo<Instance[]>(() => {
    const out: Instance[] = [];
    for (const flag of row.rest_flags) {
      for (let i = 0; i < flag.missing; i += 1) {
        out.push({ key: `${flag.date}#${i}`, date: flag.date });
      }
    }
    return out;
  }, [row.rest_flags]);

  // undefined = undecided; true = provided; false = missed. Approval is
  // disabled until EVERY instance is decided (spec AC 3).
  const [decisions, setDecisions] = useState<Record<string, boolean | undefined>>(
    {},
  );
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const allDecided = instances.every((i) => decisions[i.key] !== undefined);
  const missedCount = instances.filter((i) => decisions[i.key] === false).length;
  const owedCents = atRiskCents(missedCount, row.hourly_rate_cents);

  const setDecision = (key: string, provided: boolean): void => {
    setDecisions((prev) => ({ ...prev, [key]: provided }));
  };

  const submit = async (): Promise<void> => {
    setPending(true);
    setError(null);
    const attestations: RestAttestationInput[] = instances.map((i) => ({
      date: i.date,
      provided: decisions[i.key] === true,
    }));
    const result = await adminTimesheetsApi.approve(apiUrl, {
      staffUserId: row.staff_user_id,
      periodStart: row.period_start,
      periodEnd: row.period_end,
      attestations,
    });
    setPending(false);
    if (result.error !== undefined || !result.data) {
      setError(result.error ?? 'We could not approve this timesheet. Try again.');
      return;
    }
    onApproved(result.data.row);
  };

  return (
    <Modal
      isOpen
      onClose={onClose}
      title={`Approve timesheet — ${row.employee_name}`}
      size="md"
    >
      {blockedByOpenEntry ? (
        // Open entry → approval is blocked, but never a dead end (P2): we
        // send the manager straight to the fix flow.
        <div data-testid="approve-blocked-open-entry">
          <p className="ferro-ts-modal__lead">
            {row.employee_name} still has an open punch in this period — someone
            is clocked in without a clock-out. Close or correct it before
            approving, so the hours are true.
          </p>
          <div className="ferro-ts-modal__actions">
            <Link
              to="/timeclock/manage"
              className="ferro-btn ferro-btn--primary"
              onClick={onClose}
            >
              Go to Punch records
            </Link>
            <button
              type="button"
              className="ferro-btn ferro-btn--ghost"
              onClick={onClose}
            >
              Cancel
            </button>
          </div>
        </div>
      ) : (
        <form
          className="ferro-form"
          onSubmit={(e) => {
            e.preventDefault();
            void submit();
          }}
        >
          {instances.length === 0 ? (
            <p className="ferro-ts-modal__lead" data-testid="approve-no-flags">
              No rest periods need confirming this period. Approving locks in{' '}
              {row.employee_name}&rsquo;s hours for payroll.
            </p>
          ) : (
            <>
              <p className="ferro-ts-modal__lead">
                Colorado owes a paid 10-minute rest for stretches of work.
                Confirm what actually happened for each one — only the rests you
                mark <strong>missed</strong> become owed wages.
              </p>
              <ul className="ferro-ts-attest" data-testid="attest-list">
                {instances.map((inst, idx) => {
                  const decision = decisions[inst.key];
                  return (
                    <li
                      key={inst.key}
                      className="ferro-ts-attest__item"
                      data-testid={`attest-instance-${idx}`}
                    >
                      <span className="ferro-ts-attest__label">
                        {fmtDay(inst.date)} · rest period
                      </span>
                      <div
                        className="ferro-ts-attest__choice"
                        role="group"
                        aria-label={`Rest period on ${fmtDay(inst.date)}`}
                      >
                        <button
                          type="button"
                          className={`ferro-ts-attest__opt${
                            decision === true
                              ? ' ferro-ts-attest__opt--provided-on'
                              : ''
                          }`}
                          aria-pressed={decision === true}
                          onClick={() => setDecision(inst.key, true)}
                          data-testid={`attest-${idx}-provided`}
                        >
                          Provided
                        </button>
                        <button
                          type="button"
                          className={`ferro-ts-attest__opt${
                            decision === false
                              ? ' ferro-ts-attest__opt--missed-on'
                              : ''
                          }`}
                          aria-pressed={decision === false}
                          onClick={() => setDecision(inst.key, false)}
                          data-testid={`attest-${idx}-missed`}
                        >
                          Missed
                        </button>
                      </div>
                    </li>
                  );
                })}
              </ul>

              {/* Owed-wages preview — honest when there's no rate (P5). */}
              <div className="ferro-ts-modal__owed" data-testid="approve-owed">
                {missedCount === 0 ? (
                  <span className="ferro-muted">
                    No missed rests — nothing extra owed.
                  </span>
                ) : owedCents === null ? (
                  <span className="ferro-ts-owed--norate">
                    {missedCount} missed rest {missedCount === 1 ? 'period' : 'periods'} —
                    wages can&rsquo;t be calculated until a pay rate is set.
                  </span>
                ) : (
                  <span>
                    {missedCount} missed rest {missedCount === 1 ? 'period' : 'periods'} —{' '}
                    <strong className="ferro-ts-tnum">
                      {formatUsdCents(owedCents)}
                    </strong>{' '}
                    owed.
                  </span>
                )}
              </div>
            </>
          )}

          {error ? (
            <p className="ferro-form__error" role="alert" data-testid="approve-error">
              {error}
            </p>
          ) : null}

          <div className="ferro-ts-modal__actions">
            <button
              type="button"
              className="ferro-btn ferro-btn--ghost"
              onClick={onClose}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="ferro-btn ferro-btn--primary"
              disabled={pending || !allDecided}
              data-testid="approve-submit"
            >
              {pending ? 'Approving…' : 'Approve timesheet'}
            </button>
          </div>
        </form>
      )}
    </Modal>
  );
}
